import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import {
  FaSearch,
  FaSyncAlt,
  FaPlus,
  FaUser,
  FaUserPlus,
  FaEye,
  FaPhoneAlt,
  FaCalendarAlt,
  FaWhatsapp,
  FaFileAlt,
} from 'react-icons/fa';
import { crmMyLeadsApi, crmLeadApi } from '../../api/crmApi';
import AppDropdown from '../../components/AppDropdown';
import { LeadStatusBadge, PriorityBadge, SourceBadge } from './components/LeadStatusBadge';
import { CallModal } from './components/CallModal';
import { FollowupModal } from './components/FollowupModal';
import './CRM.css';
import './CRM_MY_LEADS.css';

const fallbackLeads = [
  { id: 1, leadId: 'LEAD-1001', customerName: 'Ankit Mehta', company: 'Mehta Oxygen Pvt. Ltd.', mobile: '9876543210', requirement: '50 Industrial Gas Cylinders', source: 'IndiaMART', assignedTo: 'Rahul Sharma', status: 'Qualified', priority: 'High', nextFollowup: '2026-10-04', lastContact: '2026-10-02' },
  { id: 4, leadId: 'LEAD-1004', customerName: 'Mohammed Ali', company: 'Ali Welding Works', mobile: '9898989898', requirement: '30 Argon Cylinders for TIG welding', source: 'Referral', assignedTo: 'Priya Verma', status: 'New', priority: 'Low', nextFollowup: '', lastContact: '' },
  { id: 6, leadId: 'LEAD-1006', customerName: 'Deepak Joshi', company: 'Joshi Fabricators', mobile: '9654433221', requirement: 'CO2 Cylinders for food industry', source: 'Manual', assignedTo: 'Rahul Sharma', status: 'Won', priority: 'Medium', nextFollowup: '', lastContact: '2026-09-30' },
];
const fallbackStats = { total: 3, newLeads: 1, contacted: 0, qualified: 1, won: 1, todayFollowups: 0, overdue: 0, conversionRate: 33 };

const fmt = (d) => d ? new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' }) : '—';

/* Resolve a value from a lead object, tolerating alternate backend key names. */
const getByPath = (obj, path) => path.split('.').reduce((acc, key) => (acc == null ? acc : acc[key]), obj);
const pickField = (obj, keys) => {
  for (const key of keys) {
    const val = getByPath(obj, key);
    if (val !== undefined && val !== null && val !== '') return val;
  }
  return '';
};
const asName = (v) => (v && typeof v === 'object' ? (v.name || v.fullName || v.username || v.email || '') : v);

/* Some list endpoints wrap each row (e.g. { lead, lastCall, nextFollowup }); merge both levels. */
const normalizeLead = (raw) => {
  if (!raw || typeof raw !== 'object') return raw;
  const lead = raw.lead && typeof raw.lead === 'object' ? raw.lead : raw;
  return {
    ...lead,
    ...raw,
    id: lead.id ?? raw.id,
    leadId: pickField(lead, ['leadId', 'leadNumber', 'code', 'referenceNo']) || pickField(raw, ['leadId', 'leadNumber', 'code']),
    customerName: pickField(lead, ['customerName', 'customer_name', 'contactPerson', 'contactName', 'name']),
    company: pickField(lead, ['company', 'companyName', 'company_name']),
    mobile: pickField(lead, ['mobile', 'mobileNumber', 'contactNumber', 'phone', 'phoneNumber']),
    requirement: pickField(lead, ['requirement', 'requirements', 'requirementDetails', 'requirement_description']),
    source: pickField(lead, ['source', 'leadSource', 'lead_source']),
    assignedTo: asName(pickField(lead, ['assignedTo', 'assignedToName', 'salesperson', 'salespersonName', 'assignedTo.name', 'assignedToUser.name', 'assignedUser.name'])),
    status: pickField(lead, ['status', 'leadStatus']),
    priority: pickField(lead, ['priority']),
    lastContact: pickField(lead, ['lastContact', 'lastContactDate', 'lastCall', 'lastCallDate', 'lastCallAt', 'lastCalledAt', 'lastContactedAt']) || pickField(raw, ['lastContact', 'lastCall', 'lastCallAt', 'lastContactDate']),
    nextFollowup: pickField(lead, ['nextFollowup', 'nextFollowupDate', 'nextFollowUp', 'nextFollowUpDate', 'followupDate', 'followUpDate', 'nextFollowupAt']) || pickField(raw, ['nextFollowup', 'nextFollowupDate', 'nextFollowUp', 'nextFollowUpDate']),
  };
};

/* Extract the row array out of common response shapes; null when unrecognised. */
const extractList = (payload) => {
  if (Array.isArray(payload)) return payload;
  if (payload && typeof payload === 'object') {
    for (const key of ['content', 'leads', 'records', 'items', 'list']) {
      if (Array.isArray(payload[key])) return payload[key];
    }
    if (Array.isArray(payload.data)) return payload.data;
    if (payload.data && typeof payload.data === 'object') return extractList(payload.data);
  }
  return null;
};

const STATUS_OPTIONS = ['New', 'Contacted', 'Interested', 'Qualified', 'Requirement', 'Quotation', 'Negotiation', 'Won', 'Lost']
  .map((s) => ({ value: s, label: s }));

const SkeletonBlock = ({ width = '100%', height = 16, radius = 8, style = {} }) => (
  <span className="crm-skeleton" style={{ width, height, borderRadius: radius, ...style }} />
);

export default function CRMMyLeads() {
  const navigate = useNavigate();
  const [leads, setLeads] = useState(fallbackLeads);
  const [stats, setStats] = useState(fallbackStats);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [callModal, setCallModal] = useState({ open: false, lead: null });
  const [followupModal, setFollowupModal] = useState({ open: false, lead: null });
  const [selectedLeads, setSelectedLeads] = useState([]);
  const fetchingRef = useRef(false);

  const fetchAll = useCallback(async () => {
    if (fetchingRef.current) return;
    fetchingRef.current = true;
    setLoading(true);
    try {
      const [lRes, sRes] = await Promise.allSettled([crmMyLeadsApi.getAll({ size: 50 }), crmMyLeadsApi.getStats()]);
      if (lRes.status === 'fulfilled') {
        const list = extractList(lRes.value?.data);
        if (Array.isArray(list)) setLeads(list.map(normalizeLead));
      }
      if (sRes.status === 'fulfilled' && sRes.value?.data) setStats({ ...fallbackStats, ...sRes.value.data });
    } catch { /* fallback */ } finally {
      fetchingRef.current = false;
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const filtered = leads.filter((l) => {
    if (statusFilter && l.status !== statusFilter) return false;
    if (search) {
      const q = search.toLowerCase();
      return (l.customerName || '').toLowerCase().includes(q) || (l.company || '').toLowerCase().includes(q) || (l.mobile || '').includes(q) || (l.leadId || '').toLowerCase().includes(q) || (l.assignedTo || '').toLowerCase().includes(q);
    }
    return true;
  });

  const toggleSelectAll = () => {
    if (selectedLeads.length === filtered.length) {
      setSelectedLeads([]);
    } else {
      setSelectedLeads(filtered.map((l) => l.id));
    }
  };

  const toggleSelectLead = (id) => {
    setSelectedLeads((prev) => (prev.includes(id) ? prev.filter((lid) => lid !== id) : [...prev, id]));
  };

  const kpis = [
    { label: 'Total Leads', value: stats.total ?? leads.length, color: '#6366F1' },
    { label: 'New', value: stats.newLeads, color: '#0EA5E9' },
    { label: 'Qualified', value: stats.qualifiedLeads ?? stats.qualified, color: '#F59E0B' },
    { label: 'Won', value: stats.won, color: '#16A34A' },
    { label: "Today's Follow-ups", value: stats.todaysFollowups ?? stats.todayFollowups, color: '#8B5CF6' },
    { label: 'Overdue', value: stats.overdueFollowups ?? stats.overdue, color: '#EF4444' },
    { label: 'Conversion Rate', value: `${stats.conversionRate ?? 0}%`, color: '#0D9488' },
  ];

  return (
    <div className="crm-page crm-my-leads-page">
      <div className="crm-header">
        <div className="crm-header-left">
          <div className="crm-page-icon" aria-hidden="true"><FaUser /></div>
          <div>
            <h1 className="crm-title">My Leads</h1>
            <p className="crm-subtitle">All your leads — manual, IndiaMART and every source</p>
          </div>
        </div>
        <div className="crm-header-actions">
          {selectedLeads.length > 0 && (
            <button
              type="button"
              className="crm-btn crm-btn-primary"
              onClick={() => navigate('/dashboard/customers/new', { state: { fromCrm: true, leadIds: selectedLeads } })}
            >
              Convert to Customer ({selectedLeads.length})
            </button>
          )}
          <button type="button" className="crm-btn crm-btn-ghost" onClick={fetchAll} disabled={loading}>
            <FaSyncAlt className={loading ? 'crm-spin' : ''} aria-hidden="true" />
            {loading ? 'Refreshing...' : 'Refresh'}
          </button>
          <button type="button" className="crm-btn crm-btn-primary" onClick={() => navigate('/crm/leads/new')}>
            <FaPlus aria-hidden="true" /> New Lead
          </button>
        </div>
      </div>

      <div className="crm-kpi-grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))' }}>
        {loading
          ? kpis.map((k) => (
            <div className="crm-kpi-card" key={k.label}>
              <div style={{ width: '100%' }}>
                <SkeletonBlock width="50%" height={20} style={{ marginBottom: 8 }} />
                <SkeletonBlock width="75%" height={12} radius={6} />
              </div>
            </div>
          ))
          : kpis.map((k) => (
            <div className="crm-kpi-card" key={k.label}>
              <div>
                <div className="crm-kpi-value" style={{ color: k.color }}>{k.value}</div>
                <div className="crm-kpi-label">{k.label}</div>
              </div>
            </div>
          ))}
      </div>

      <div className="crm-my-leads-filter-card">
        <div className="crm-my-leads-search-box">
          <FaSearch className="crm-search-icon" aria-hidden="true" />
          <input
            type="text"
            className="crm-my-leads-search-input"
            aria-label="Search my leads"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search my leads..."
            autoComplete="off"
            spellCheck="false"
          />
          {search && (
            <button type="button" className="crm-search-clear" onClick={() => setSearch('')} aria-label="Clear search" title="Clear search">
              ×
            </button>
          )}
        </div>
        <div className="crm-status-filter" role="group" aria-label="Filter leads by status">
          <AppDropdown
            value={statusFilter}
            options={STATUS_OPTIONS}
            onChange={setStatusFilter}
            placeholder="All Status"
            menuWidth={240}
            menuClassName="crm-dropdown-window"
          />
        </div>
      </div>

      <div className="crm-table-wrap">
        <table className="crm-table">
          <thead>
            <tr>
              <th style={{ width: '40px' }}>
                <input
                  type="checkbox"
                  checked={filtered.length > 0 && selectedLeads.length === filtered.length}
                  onChange={toggleSelectAll}
                  aria-label="Select all leads"
                />
              </th>
              <th>Lead ID</th>
              <th>Customer</th>
              <th>Company</th>
              <th>Mobile</th>
              <th>Requirement</th>
              <th>Source</th>
              <th>Assigned To</th>
              <th>Status</th>
              <th>Priority</th>
              <th>Last Contact</th>
              <th>Next Follow-up</th>
              <th>Convert to Customer</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              [...Array(6)].map((_, r) => (
                <tr key={`skeleton-${r}`}>
                  <td><SkeletonBlock width={16} height={16} radius={4} /></td>
                  {[...Array(13)].map((_, c) => (
                    <td key={c}><SkeletonBlock width={c === 0 ? 80 : '85%'} height={14} radius={7} /></td>
                  ))}
                </tr>
              ))
            ) : (
              <>
            {filtered.map((lead) => (
              <tr key={lead.id}>
                <td>
                  <input
                    type="checkbox"
                    checked={selectedLeads.includes(lead.id)}
                    onChange={() => toggleSelectLead(lead.id)}
                    aria-label={`Select ${lead.customerName}`}
                  />
                </td>
                <td className="link-cell" onClick={() => navigate(`/crm/leads/${lead.id}`)}>{lead.leadId}</td>
                <td><strong>{lead.customerName}</strong></td>
                <td>{lead.company}</td>
                <td style={{ whiteSpace: 'nowrap' }}>{lead.mobile}</td>
                <td style={{ maxWidth: 190, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{lead.requirement}</td>
                <td><SourceBadge source={lead.source} /></td>
                <td>{lead.assignedTo || '-'}</td>
                <td><LeadStatusBadge status={lead.status} /></td>
                <td><PriorityBadge priority={lead.priority} /></td>
                <td>{fmt(lead.lastContact)}</td>
                <td>{fmt(lead.nextFollowup)}</td>
                <td>
                  <button
                    type="button"
                    className="crm-convert-btn"
                    title="Convert to Customer"
                    onClick={() => navigate('/dashboard/customers/new', { state: { fromCrm: true, leadId: lead.id, lead } })}
                  >
                    <FaUserPlus aria-hidden="true" /> Convert to Customer
                  </button>
                </td>
                <td>
                  <div className="crm-actions">
                    <button type="button" className="crm-icon-btn view" title="View" aria-label="View lead" onClick={() => navigate(`/crm/leads/${lead.id}`)}><FaEye aria-hidden="true" /></button>
                    <button type="button" className="crm-icon-btn call" title="Call" aria-label="Call lead" onClick={() => setCallModal({ open: true, lead })}><FaPhoneAlt aria-hidden="true" /></button>
                    <button type="button" className="crm-icon-btn followup" title="Follow-up" aria-label="Create follow-up" onClick={() => setFollowupModal({ open: true, lead })}><FaCalendarAlt aria-hidden="true" /></button>
                    <button type="button" className="crm-icon-btn whatsapp" title="WhatsApp" aria-label="Open WhatsApp" onClick={() => navigate('/crm/whatsapp', { state: { leadId: lead.id } })}><FaWhatsapp aria-hidden="true" /></button>
                    <button type="button" className="crm-icon-btn quote" title="Quotation" aria-label="Create quotation" onClick={() => navigate('/dashboard/estimates/new', { state: { crmLeadId: lead.id, crmLead: lead } })}><FaFileAlt aria-hidden="true" /></button>
                  </div>
                </td>
              </tr>
            ))}
            {!filtered.length && (
              <tr><td colSpan={14}>
                <div className="crm-empty">
                  <div className="crm-empty-icon" aria-hidden="true"><FaUser /></div>
                  <div className="crm-empty-title">No leads yet</div>
                  <div className="crm-empty-desc">Leads you add manually or receive from IndiaMART will appear here.</div>
                </div>
              </td></tr>
            )}
              </>
            )}
          </tbody>
        </table>
      </div>

      <CallModal open={callModal.open} lead={callModal.lead} onClose={() => setCallModal({ open: false, lead: null })}
        onSaved={fetchAll}
        onSave={async (f) => { try { await crmLeadApi.addCall(callModal.lead.id, f); } catch { throw new Error('save-failed'); } toast.success('Call logged.'); setCallModal({ open: false, lead: null }); }} />
      <FollowupModal open={followupModal.open} lead={followupModal.lead} onClose={() => setFollowupModal({ open: false, lead: null })}
        onSave={async (f) => { try { await crmLeadApi.addFollowup(followupModal.lead.id, f); } catch { throw new Error('save-failed'); } toast.success('Follow-up created.'); setFollowupModal({ open: false, lead: null }); }} />
    </div>
  );
}
